import { Tour } from "nextstepjs";

export const PLAYGROUND_TOUR_NAME = "playground-walkthrough";

/**
 * Single source of truth for every DOM id the playground tour targets.
 * Components that own a tour-targeted element should consume these
 * constants (typically via the `<TourTargetContainer>` wrapper) instead
 * of hardcoding `id="tour-..."` strings — this keeps the step selectors
 * below and the rendered DOM in lockstep.
 */
export const TOUR_TARGET_IDS = {
  SYSTEM_MESSAGE: "tour-system-message",
  USER_MESSAGE: "tour-user-message",
  INPUT_VARIABLE_SETS: "tour-input-variable-sets",
  RUN_BUTTON: "tour-run-button",
  OUTPUTS: "tour-outputs",
  MODE_SWITCHER: "tour-mode-switcher",
  OUTPUT_WORKFLOW: "tour-output-workflow",
  EVALUATE_WORKFLOW: "tour-evaluate-workflow",
  CHAT_WORKFLOW: "tour-chat-workflow",
  MODEL_PROVIDER_SECTION: "tour-model-provider-section",
  SAVE_TEMPLATE: "tour-save-template",
} as const;

export type TourTargetId = (typeof TOUR_TARGET_IDS)[keyof typeof TOUR_TARGET_IDS];

export const PLAYGROUND_TOUR_STEP_INDICES = {
  SYSTEM_MESSAGE: 0,
  USER_MESSAGE: 1,
  INPUT_VARIABLE_SETS: 2,
  RUN: 3,
  OUTPUT_RESULTS: 4,
  OUTPUT_WORKFLOW: 5,
  EVALUATE_WORKFLOW: 6,
  CHAT_WORKFLOW: 7,
  MODEL_SELECTOR: 8,
  SAVE_TEMPLATE: 9,
} as const;

/**
 * Marks a scroll container so `useTourScrollLock` can block user
 * wheel/touch on it during the tour. Spread on the JSX element:
 * `<div {...TOUR_LOCK_SCROLL_PROPS} className="overflow-y-auto">`. The
 * raw attribute name is also exported for the query selector inside the
 * scroll lock hook so both sides stay in sync.
 */
export const TOUR_LOCK_SCROLL_ATTR = "data-tour-lock-scroll" as const;
export const TOUR_LOCK_SCROLL_PROPS = { [TOUR_LOCK_SCROLL_ATTR]: "" } as const;

const sel = (id: TourTargetId): string => `#${id}`;

export const playgroundTourSteps: Tour[] = [
  {
    tour: PLAYGROUND_TOUR_NAME,
    steps: [
      {
        icon: null,
        title: "System Message",
        content:
          "Define the role, tone, and guardrails once. Every test—and every production call—uses the same instructions, keeping behavior focused and consistent.",
        selector: sel(TOUR_TARGET_IDS.SYSTEM_MESSAGE),
        side: "right",
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "User Message",
        content:
          "Write the request your application will send. Turn changing data into variables with curly braces so one prompt can handle every real-world input.",
        selector: sel(TOUR_TARGET_IDS.USER_MESSAGE),
        side: "right",
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Test Real Scenarios",
        content:
          "Each input set is a reusable test case. Add happy paths and edge cases now, then rerun the same scenarios after every prompt change.",
        selector: sel(TOUR_TARGET_IDS.INPUT_VARIABLE_SETS),
        side: "left",
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Run Every Test",
        content:
          "Run all input sets in parallel. You get broad feedback in one pass instead of testing the same prompt manually, one scenario at a time.",
        selector: sel(TOUR_TARGET_IDS.RUN_BUTTON),
        side: "bottom-right",
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Compare the Results",
        content:
          "Review every response side by side. Compare quality, latency, token usage, and cost to see exactly how each prompt change performs.",
        selector: sel(TOUR_TARGET_IDS.OUTPUTS),
        side: "left",
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Output: Iterate Fast",
        content:
          "Use Output for the fastest prompt feedback loop. Run a set of scenarios, compare the responses, make a change, and repeat.",
        selector: sel(TOUR_TARGET_IDS.OUTPUT_WORKFLOW),
        side: "right",
        pointerPadding: 6,
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Evaluate: Measure Quality",
        content:
          "Use Evaluate when quality needs an objective signal. Connect a sheet of examples, map its columns to your inputs, and score this draft against its rows.",
        selector: sel(TOUR_TARGET_IDS.EVALUATE_WORKFLOW),
        side: "right",
        pointerPadding: 6,
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Chat: Test Conversations",
        content:
          "Use Chat to test multi-turn behavior. See how the model handles follow-ups, context, tool calls, and longer conversations before you ship.",
        selector: sel(TOUR_TARGET_IDS.CHAT_WORKFLOW),
        side: "right",
        pointerPadding: 6,
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Choose the Right Model",
        content:
          "Compare providers and models without rewriting your prompt. Find the best balance of quality, speed, and cost for this exact use case.",
        selector: sel(TOUR_TARGET_IDS.MODEL_PROVIDER_SECTION),
        side: "bottom",
        showControls: true,
        showSkip: true,
      },
      {
        icon: null,
        title: "Save to the Prompt Registry",
        content:
          "When you're ready, select Save Template. This creates a versioned prompt in the Prompt Registry so your team can find it, manage its history, and return to it later.",
        selector: sel(TOUR_TARGET_IDS.SAVE_TEMPLATE),
        side: "bottom",
        showControls: true,
        showSkip: true,
      },
    ],
  },
];
